import apiClient from "@/api/client";
import type { CapacityInfo } from "@/types";

export interface NodeMetrics {
  node: string;
  cpu_usage: number;
  ram_usage: number;
  disk_usage: number;
  network_in: number;
  network_out: number;
  uptime: number;
  status: "online" | "offline" | "maintenance";
  timestamp: string;
}

export interface VMMetrics {
  vmid: number;
  cpu_usage: number;
  ram_usage: number;
  disk_read: number;
  disk_write: number;
  net_in: number;
  net_out: number;
  timestamp: string;
}

export interface Alert {
  id: string;
  severity: "critical" | "warning" | "info";
  title: string;
  message: string;
  source: string;
  acknowledged: boolean;
  created_at: string;
  resolved_at: string | null;
}

interface NodeCapacity {
  node: string;
  total_cpu: number;
  used_cpu: number;
  free_cpu: number;
  total_ram_gb: number;
  used_ram_gb: number;
  free_ram_gb: number;
  total_disk_gb: number;
  used_disk_gb: number;
  free_disk_gb: number;
}

interface CapacityResponse {
  nodes: NodeCapacity[];
  cluster_free_cpu: number;
  cluster_free_ram_gb: number;
  cluster_free_disk_gb: number;
}

export async function getNodeMetrics(): Promise<NodeMetrics[]> {
  const { data } = await apiClient.get<CapacityResponse>("/monitoring/nodes");
  return data.nodes.map((n) => ({
    node: n.node,
    cpu_usage: n.total_cpu > 0 ? (n.used_cpu / n.total_cpu) * 100 : 0,
    ram_usage:
      n.total_ram_gb > 0 ? (n.used_ram_gb / n.total_ram_gb) * 100 : 0,
    disk_usage:
      n.total_disk_gb > 0 ? (n.used_disk_gb / n.total_disk_gb) * 100 : 0,
    network_in: 0,
    network_out: 0,
    uptime: 0,
    status: "online" as const,
    timestamp: new Date().toISOString(),
  }));
}

export async function getVMMetrics(vmId: number): Promise<VMMetrics[]> {
  const { data } = await apiClient.get<VMMetrics[]>(
    `/monitoring/vms/${vmId}/metrics`
  );
  return data;
}

export async function getAlerts(): Promise<Alert[]> {
  const { data } = await apiClient.get<Alert[]>("/monitoring/alerts");
  return data;
}

export async function acknowledgeAlert(alertId: string): Promise<void> {
  await apiClient.post(`/monitoring/alerts/${alertId}/acknowledge`);
}

export async function getCapacity(): Promise<CapacityInfo[]> {
  const { data } = await apiClient.get<CapacityResponse>("/monitoring/nodes");
  return data.nodes.map((n) => ({
    node: n.node,
    total_cpu: n.total_cpu,
    used_cpu: n.used_cpu,
    total_ram_mb: n.total_ram_gb * 1024,
    used_ram_mb: n.used_ram_gb * 1024,
    total_disk_gb: n.total_disk_gb,
    used_disk_gb: n.used_disk_gb,
    vm_count: 0,
    status: "online" as const,
  }));
}
